import React from 'react';
import { MerchantType } from '../types';

interface Step {
  id: string;
  label: string;
  description: string;
  allowBack?: boolean;
}

interface WizardStepperProps {
  currentStep: number;
  merchantType: MerchantType;
  onStepClick?: (step: number) => void;
}

const WizardStepper: React.FC<WizardStepperProps> = ({
  currentStep,
  merchantType,
  onStepClick,
}) => {
  const steps: Step[] = [
    {
      id: 'merchant',
      label: 'Merchant',
      description: 'Basic information',
      allowBack: true,
    },
    {
      id: merchantType === 'regular' ? 'subaccounts' : 'agents',
      label: merchantType === 'regular' ? 'Subaccounts' : 'Agents',
      description: merchantType === 'regular' ? 'Configure subaccounts' : 'Configure agents',
      allowBack: true,
    },
    {
      id: 'review',
      label: 'Review',
      description: 'Confirm & submit',
      allowBack: true,
    },
    {
      id: 'progress',
      label: 'Progress',
      description: 'Creating entities',
      allowBack: false,
    },
    {
      id: 'complete',
      label: 'Complete',
      description: 'Setup finished',
      allowBack: false,
    },
  ];

  return (
    <nav aria-label="Progress" className="mb-8">
      <ol className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = index < currentStep;
          const isCurrent = index === currentStep;
          // Only allow clicking on completed steps that allow back navigation
          const isClickable = index < currentStep && onStepClick && step.allowBack && currentStep < 3;
          const isLast = index === steps.length - 1;

          return (
            <li
              key={step.id}
              className={`relative flex items-center ${!isLast ? 'flex-1' : ''}`}
            >
              {/* Step circle and label */}
              <div
                className={`relative z-10 flex flex-col items-center ${
                  isClickable ? 'cursor-pointer' : ''
                }`}
                onClick={() => isClickable && onStepClick(index)}
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                    isCompleted
                      ? 'bg-primary-600 text-white'
                      : isCurrent
                      ? 'border-2 border-primary-600 bg-white text-primary-600'
                      : 'border-2 border-gray-300 bg-white text-gray-400'
                  } ${isClickable ? 'hover:border-primary-400' : ''}`}
                >
                  {isCompleted ? (
                    <svg
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </span>

                <div className="mt-2 text-center">
                  <span
                    className={`block text-sm font-medium ${
                      isCurrent ? 'text-primary-600' : isCompleted ? 'text-gray-900' : 'text-gray-500'
                    }`}
                  >
                    {step.label}
                  </span>
                  <span className="block text-xs text-gray-400">
                    {step.description}
                  </span>
                </div>
              </div>

              {/* Connector line */}
              {!isLast && (
                <div className="flex-1 mx-4">
                  <div
                    className={`h-0.5 w-full ${
                      isCompleted ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default WizardStepper;
